import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Trophy, Star, Lock, Gift, ChevronRight, Copy, Check, MapPin, Zap, LogIn } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// V5 Design Constants
const v5 = {
  colors: {
    navy: '#1e1b4b',
    cream: '#FEF7ED',
    white: '#FFFFFF',
    purple: '#8b5cf6',
    purpleDark: '#7c3aed',
    orange: '#f97316',
    orangeDark: '#ea580c',
    green: '#22c55e',
    amber: '#f59e0b',
    textPrimary: '#1e1b4b',
    textSecondary: 'rgba(30, 27, 75, 0.7)',
    textMuted: 'rgba(30, 27, 75, 0.5)',
    textHint: 'rgba(30, 27, 75, 0.4)',
    borderLight: 'rgba(30, 27, 75, 0.05)',
  },
  fonts: {
    display: "'Faustina', Georgia, serif",
    body: "'DM Sans', sans-serif",
    mono: "'JetBrains Mono', monospace"
  }
};

// ============================================
// DYNAMIC BACKGROUND - Option 2: Subtle Code Grid Pattern
// ============================================
const CodeGridBackground = () => {
  // Code snippets that float around
  const codeSnippets = [
    '{ taps: 100 }',
    'earn()',
    '// rewards',
    'hub.join()',
    '=> prize',
    'quest++',
    '[ ...deals ]',
    'rank: #1'
  ];

  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
      {/* Subtle grid pattern */}
      <div 
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(30, 27, 75, 0.5) 1px, transparent 1px),
            linear-gradient(90deg, rgba(30, 27, 75, 0.5) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
        }}
      />
      
      {/* Floating code snippets */}
      {codeSnippets.map((snippet, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0 }}
          animate={{ 
            opacity: [0.03, 0.08, 0.03],
            y: [0, -20, 0],
            x: [0, index % 2 === 0 ? 10 : -10, 0],
          }}
          transition={{
            duration: 8 + index * 2,
            repeat: Infinity,
            ease: "easeInOut",
            delay: index * 1.5,
          }}
          className="absolute text-xs whitespace-nowrap"
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            color: index % 3 === 0 ? v5.colors.purple : index % 3 === 1 ? v5.colors.orange : v5.colors.navy,
            top: `${15 + (index * 12) % 70}%`,
            left: `${5 + (index * 15) % 85}%`,
            fontSize: '11px',
          }}
        >
          {snippet}
        </motion.div>
      ))}
      
      {/* Gradient overlay for depth */}
      <div 
        className="absolute inset-0"
        style={{
          background: `radial-gradient(ellipse at 30% 20%, rgba(139, 92, 246, 0.05) 0%, transparent 50%),
                       radial-gradient(ellipse at 70% 80%, rgba(249, 115, 22, 0.05) 0%, transparent 50%)`,
        }}
      />
    </div>
  );
};

const Rewards = () => {
  const { isAuthenticated, user, loading: authLoading, openAuthModal } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tapsBalance, setTapsBalance] = useState(null);
  const [unlockedHubs, setUnlockedHubs] = useState([]);
  const [quests, setQuests] = useState([]);
  const [referralStats, setReferralStats] = useState(null);
  const [copiedCode, setCopiedCode] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (isAuthenticated) {
      fetchRewardsData();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, authLoading]);

  const fetchRewardsData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [balanceRes, hubsRes, questsRes, referralRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/taps/balance`, { headers }),
        axios.get(`${BACKEND_URL}/api/taps/unlocked-hubs`, { headers }),
        axios.get(`${BACKEND_URL}/api/quests/all`, { headers }),
        axios.get(`${BACKEND_URL}/api/referrals/stats`, { headers })
      ]);

      setTapsBalance(balanceRes.data);
      setUnlockedHubs(hubsRes.data.hubs || []);
      setQuests(questsRes.data.quests || []);
      setReferralStats(referralRes.data);
    } catch (err) {
      console.error('Error fetching rewards data:', err);
      setError('Failed to load rewards data');
    } finally {
      setLoading(false);
    }
  };

  const copyReferralCode = () => {
    if (tapsBalance?.referral_code) {
      navigator.clipboard.writeText(tapsBalance.referral_code);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const getTapsActions = () => [
    { action: 'Scan a coaster', taps: 10, description: 'First scan at each hub daily', icon: '📱' },
    { action: 'Claim an offer', taps: 25, description: 'Save a deal to your wallet', icon: '🎟️' },
    { action: 'Redeem an offer', taps: 50, description: 'Use a deal at a business', icon: '✅' },
    { action: 'Complete monthly quest', taps: 200, description: '3 redemptions at one hub', icon: '🏆' },
    { action: 'Refer a friend', taps: 100, description: 'When they sign up', icon: '👥' },
  ];

  // Loading state
  if (authLoading || (isAuthenticated && loading)) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: v5.colors.cream }}>
        <div 
          className="animate-spin rounded-full h-12 w-12 border-4 border-t-transparent"
          style={{ borderColor: v5.colors.purple, borderTopColor: 'transparent' }}
        ></div>
      </div>
    );
  }

  // Unauthenticated state - with Hero
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: v5.colors.cream }}>
        {/* V5 Hero Section */}
        <HeroSection tapsBalance={0} unlockedHubsCount={0} questsCompleted={0} />

        {/* Login Prompt */}
        <div className="max-w-lg mx-auto px-4 py-16 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-xl p-8"
            style={{ 
              backgroundColor: v5.colors.white,
              border: `1px solid ${v5.colors.borderLight}`,
              boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)',
              borderLeft: `4px solid ${v5.colors.orange}`
            }}
          >
            <p style={{ fontFamily: v5.fonts.mono, fontSize: '12px', color: v5.colors.textHint, marginBottom: '16px' }}>
              // join_rewards
            </p>
            <div 
              className="w-20 h-20 mx-auto mb-6 rounded-xl flex items-center justify-center"
              style={{ background: `linear-gradient(135deg, ${v5.colors.orange} 0%, ${v5.colors.amber} 100%)` }}
            >
              <Trophy className="w-10 h-10 text-white" />
            </div>
            <h2 style={{ fontFamily: v5.fonts.display, fontSize: '24px', fontWeight: 500, color: v5.colors.textPrimary, marginBottom: '12px' }}>
              Join Tappd Rewards
            </h2>
            <p style={{ fontFamily: v5.fonts.body, color: v5.colors.textMuted, marginBottom: '24px' }}>
              Sign in to start earning Taps and compete for monthly prizes at your favorite local spots!
            </p>
            
            {/* Prize Preview */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              {[
                { emoji: '🥇', prize: '$100' },
                { emoji: '🥈', prize: '$50' },
                { emoji: '🥉', prize: '$25' }
              ].map((item, idx) => (
                <div 
                  key={idx}
                  className="text-center p-3 rounded-xl"
                  style={{ backgroundColor: v5.colors.cream }}
                >
                  <div className="text-2xl mb-1">{item.emoji}</div>
                  <p style={{ fontFamily: v5.fonts.body, fontWeight: 700, fontSize: '14px', color: v5.colors.textPrimary }}>
                    {item.prize}
                  </p>
                </div>
              ))}
            </div>

            <button
              onClick={() => openAuthModal('login')}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-white transition-all hover:-translate-y-0.5"
              style={{ 
                background: `linear-gradient(135deg, ${v5.colors.orange} 0%, ${v5.colors.orangeDark} 100%)`,
                fontFamily: v5.fonts.body,
                boxShadow: '0 4px 15px rgba(249, 115, 22, 0.4)'
              }}
            >
              <LogIn className="w-5 h-5" />
              Sign In to Get Started
            </button>
            
            <p style={{ fontFamily: v5.fonts.body, fontSize: '14px', color: v5.colors.textMuted, marginTop: '16px' }}>
              Don&apos;t have an account?{' '}
              <button 
                onClick={() => openAuthModal('signup')}
                style={{ color: v5.colors.orange, fontWeight: 600 }}
                className="hover:underline"
              >
                Sign up free
              </button>
            </p>
          </motion.div>
        </div>
      </div>
    );
  }

  // Authenticated view
  const questsCompleted = quests.filter(q => q.quest_completed).length;

  return (
    <div className="min-h-screen" style={{ backgroundColor: v5.colors.cream }}>
      {/* V5 Hero Section */}
      <HeroSection 
        tapsBalance={tapsBalance?.total_balance || 0} 
        unlockedHubsCount={unlockedHubs.length}
        questsCompleted={questsCompleted}
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {/* How to Earn Taps */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="rounded-xl p-6"
          style={{ 
            backgroundColor: v5.colors.white,
            border: `1px solid ${v5.colors.borderLight}`,
            boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)',
            borderLeft: `4px solid ${v5.colors.purple}`
          }}
        >
          <p style={{ fontFamily: v5.fonts.mono, fontSize: '12px', color: v5.colors.textHint, marginBottom: '12px' }}>
            // earn_taps
          </p>
          <h2 className="flex items-center gap-2 mb-4" style={{ fontFamily: v5.fonts.display, fontSize: '20px', fontWeight: 500, color: v5.colors.textPrimary }}>
            <Star className="w-5 h-5" style={{ color: v5.colors.purple }} />
            How to Earn Taps
          </h2>
          
          <div className="space-y-3">
            {getTapsActions().map((item, idx) => (
              <div 
                key={idx}
                className="flex items-center justify-between p-3 rounded-xl transition-colors"
                style={{ backgroundColor: v5.colors.cream }}
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{item.icon}</span>
                  <div>
                    <p style={{ fontFamily: v5.fonts.body, fontWeight: 600, fontSize: '14px', color: v5.colors.textPrimary }}>
                      {item.action}
                    </p>
                    <p style={{ fontFamily: v5.fonts.body, fontSize: '12px', color: v5.colors.textMuted }}>
                      {item.description}
                    </p>
                  </div>
                </div>
                <div 
                  className="px-3 py-1 rounded-full"
                  style={{ background: `linear-gradient(135deg, ${v5.colors.purple} 0%, ${v5.colors.purpleDark} 100%)` }}
                >
                  <span style={{ fontFamily: v5.fonts.body, color: v5.colors.white, fontWeight: 700, fontSize: '14px' }}>
                    +{item.taps}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Referral Card */}
        {tapsBalance?.referral_code && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="rounded-xl p-6 text-white"
            style={{ backgroundColor: v5.colors.navy }}
          >
            <p style={{ fontFamily: v5.fonts.mono, fontSize: '12px', color: 'rgba(255, 255, 255, 0.4)', marginBottom: '12px' }}>
              // share_and_earn
            </p>
            <h2 className="flex items-center gap-2 mb-2" style={{ fontFamily: v5.fonts.display, fontSize: '20px', fontWeight: 500 }}>
              <Gift className="w-5 h-5" style={{ color: v5.colors.orange }} />
              Share & Earn
            </h2>
            <p style={{ fontFamily: v5.fonts.body, fontSize: '14px', color: 'rgba(255, 255, 255, 0.7)', marginBottom: '16px' }}>
              Invite friends and earn 100 Taps when they sign up. They get 50 Taps too!
            </p>
            
            <div className="flex items-center gap-3">
              <div 
                className="flex-1 px-4 py-3 rounded-xl text-lg tracking-wider"
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', fontFamily: v5.fonts.mono }}
              >
                {tapsBalance.referral_code}
              </div>
              <button
                onClick={copyReferralCode}
                className="px-4 py-3 rounded-xl font-semibold flex items-center gap-2 transition-all hover:-translate-y-0.5"
                style={{ 
                  background: `linear-gradient(135deg, ${v5.colors.orange} 0%, ${v5.colors.orangeDark} 100%)`,
                  fontFamily: v5.fonts.body
                }}
              >
                {copiedCode ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                {copiedCode ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </motion.div>
        )}

        {/* Unlocked Hubs & Leaderboards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="rounded-xl p-6"
          style={{ 
            backgroundColor: v5.colors.white,
            border: `1px solid ${v5.colors.borderLight}`,
            boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)',
            borderLeft: `4px solid ${v5.colors.orange}`
          }}
        >
          <p style={{ fontFamily: v5.fonts.mono, fontSize: '12px', color: v5.colors.textHint, marginBottom: '12px' }}>
            // your_hubs
          </p>
          <div className="flex items-center justify-between mb-4">
            <h2 className="flex items-center gap-2" style={{ fontFamily: v5.fonts.display, fontSize: '20px', fontWeight: 500, color: v5.colors.textPrimary }}>
              <MapPin className="w-5 h-5" style={{ color: v5.colors.orange }} />
              Your Hubs
            </h2>
            <span style={{ fontFamily: v5.fonts.body, fontSize: '14px', color: v5.colors.textMuted }}>
              {unlockedHubs.length} unlocked
            </span>
          </div>

          {unlockedHubs.length === 0 ? (
            <div className="text-center py-8">
              <div 
                className="w-16 h-16 mx-auto mb-4 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: v5.colors.cream }}
              >
                <Lock className="w-8 h-8" style={{ color: v5.colors.textHint }} />
              </div>
              <p style={{ fontFamily: v5.fonts.body, color: v5.colors.textMuted }}>
                Scan a coaster at a hub to unlock it!
              </p>
              <Link 
                to="/local-deals-near-me"
                className="inline-flex items-center gap-2 mt-4 font-semibold hover:underline"
                style={{ fontFamily: v5.fonts.body, color: v5.colors.orange }}
              >
                Find deals near you
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {unlockedHubs.map((hub, idx) => (
                <Link
                  key={hub.hub_id}
                  to={`/leaderboard/${hub.hub_id}`}
                  className="flex items-center justify-between p-4 rounded-xl transition-all group"
                  style={{ backgroundColor: v5.colors.cream }}
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold"
                      style={{ background: `linear-gradient(135deg, ${v5.colors.orange} 0%, ${v5.colors.amber} 100%)`, fontFamily: v5.fonts.body }}
                    >
                      {idx + 1}
                    </div>
                    <div>
                      <p style={{ fontFamily: v5.fonts.body, fontWeight: 600, color: v5.colors.textPrimary }}>{hub.name}</p>
                      <p style={{ fontFamily: v5.fonts.body, fontSize: '12px', color: v5.colors.textMuted }}>
                        {hub.city}, {hub.state}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p style={{ fontFamily: v5.fonts.body, fontWeight: 700, color: v5.colors.orange }}>{hub.monthly_taps} Taps</p>
                      <p style={{ fontFamily: v5.fonts.body, fontSize: '12px', color: v5.colors.textMuted }}>this month</p>
                    </div>
                    <ChevronRight className="w-5 h-5 transition-colors" style={{ color: v5.colors.textHint }} />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </motion.div>

        {/* Monthly Quests */}
        {quests.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="rounded-xl p-6"
            style={{ 
              backgroundColor: v5.colors.white,
              border: `1px solid ${v5.colors.borderLight}`,
              boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)',
              borderLeft: `4px solid ${v5.colors.green}`
            }}
          >
            <p style={{ fontFamily: v5.fonts.mono, fontSize: '12px', color: v5.colors.textHint, marginBottom: '12px' }}>
              // monthly_quests
            </p>
            <h2 className="flex items-center gap-2 mb-4" style={{ fontFamily: v5.fonts.display, fontSize: '20px', fontWeight: 500, color: v5.colors.textPrimary }}>
              🎯 Monthly Quests
            </h2>
            <p style={{ fontFamily: v5.fonts.body, fontSize: '14px', color: v5.colors.textMuted, marginBottom: '16px' }}>
              Redeem 3 deals at any hub this month to earn a +200 Taps bonus!
            </p>
            
            <div className="space-y-3">
              {quests.map((quest) => (
                <div
                  key={quest.hub_id}
                  className="p-4 rounded-xl"
                  style={{ 
                    backgroundColor: quest.quest_completed ? 'rgba(34, 197, 94, 0.1)' : v5.colors.cream,
                    border: quest.quest_completed ? `1px solid ${v5.colors.green}` : 'none'
                  }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{quest.quest_completed ? '✅' : '🎯'}</span>
                      <span style={{ fontFamily: v5.fonts.body, fontWeight: 600, color: v5.colors.textPrimary }}>
                        {quest.hub_name}
                      </span>
                    </div>
                    {quest.quest_completed && (
                      <span 
                        className="px-2 py-1 text-xs font-bold rounded-full text-white"
                        style={{ backgroundColor: v5.colors.green }}
                      >
                        +200 Taps
                      </span>
                    )}
                  </div>
                  
                  <div className="w-full rounded-full h-2 mb-1" style={{ backgroundColor: 'rgba(30, 27, 75, 0.1)' }}>
                    <div
                      className="h-2 rounded-full transition-all"
                      style={{ 
                        width: `${Math.min(quest.redemptions_count / quest.quest_target * 100, 100)}%`,
                        backgroundColor: quest.quest_completed ? v5.colors.green : v5.colors.orange
                      }}
                    />
                  </div>
                  <p style={{ fontFamily: v5.fonts.body, fontSize: '12px', color: v5.colors.textMuted, textAlign: 'right' }}>
                    {quest.redemptions_count} / {quest.quest_target} redemptions
                  </p>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Monthly Prizes Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="rounded-xl p-6"
          style={{ 
            background: `linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(249, 115, 22, 0.1) 100%)`,
            border: `2px solid rgba(249, 115, 22, 0.2)`
          }}
        >
          <p style={{ fontFamily: v5.fonts.mono, fontSize: '12px', color: v5.colors.textHint, marginBottom: '12px' }}>
            // monthly_prizes
          </p>
          <h2 className="flex items-center gap-2 mb-4" style={{ fontFamily: v5.fonts.display, fontSize: '20px', fontWeight: 500, color: v5.colors.textPrimary }}>
            <Trophy className="w-5 h-5" style={{ color: v5.colors.amber }} />
            Monthly Prizes
          </h2>
          
          <div className="grid grid-cols-3 gap-4 mb-4">
            {[
              { emoji: '🥇', prize: '$100', place: '1st Place' },
              { emoji: '🥈', prize: '$50', place: '2nd Place' },
              { emoji: '🥉', prize: '$25', place: '3rd Place' }
            ].map((item, idx) => (
              <div 
                key={idx}
                className="text-center p-4 rounded-xl"
                style={{ backgroundColor: v5.colors.white, boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)' }}
              >
                <div className="text-3xl mb-2">{item.emoji}</div>
                <p style={{ fontFamily: v5.fonts.body, fontWeight: 700, fontSize: '18px', color: v5.colors.textPrimary }}>
                  {item.prize}
                </p>
                <p style={{ fontFamily: v5.fonts.body, fontSize: '12px', color: v5.colors.textMuted }}>
                  {item.place}
                </p>
              </div>
            ))}
          </div>
          
          <p style={{ fontFamily: v5.fonts.body, fontSize: '14px', color: v5.colors.textSecondary, textAlign: 'center' }}>
            Winners announced on the 1st of each month. Gift cards provided by venue partners.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

// ============================================
// HERO SECTION COMPONENT
// ============================================
const HeroSection = ({ tapsBalance, unlockedHubsCount, questsCompleted }) => {
  // Code snippets for the grid background
  const codeSnippets = [
    '{ taps: 100 }',
    'earn()',
    '// rewards',
    'hub.join()',
    '=> prize',
    'quest++',
  ];

  return (
    <section className="relative overflow-hidden" style={{ paddingTop: '8rem' }}>
      {/* Navy background */}
      <div style={{ 
        position: 'absolute', 
        inset: 0, 
        backgroundColor: v5.colors.navy,
        boxShadow: '0 4px 20px rgba(30, 27, 75, 0.3)'
      }}></div>
      
      {/* OPTION 2: Code Grid Pattern - Inside Hero */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Subtle grid pattern - 10% more visible */}
        <div 
          className="absolute inset-0 opacity-[0.09]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255, 255, 255, 0.3) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255, 255, 255, 0.3) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
          }}
        />
        
        {/* Floating code snippets */}
        {codeSnippets.map((snippet, index) => (
          <motion.div
            key={index}
            animate={{ 
              opacity: [0.1, 0.25, 0.1],
              y: [0, -15, 0],
            }}
            transition={{
              duration: 6 + index * 1.5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: index * 1,
            }}
            className="absolute text-xs whitespace-nowrap"
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              color: index % 2 === 0 ? 'rgba(139, 92, 246, 0.5)' : 'rgba(249, 115, 22, 0.5)',
              top: `${15 + (index * 15) % 70}%`,
              left: `${5 + (index * 18) % 85}%`,
              fontSize: '11px',
            }}
          >
            {snippet}
          </motion.div>
        ))}
        
        {/* Gradient accents */}
        <div 
          className="absolute inset-0"
          style={{
            background: `radial-gradient(ellipse at 20% 30%, rgba(139, 92, 246, 0.15) 0%, transparent 50%),
                         radial-gradient(ellipse at 80% 70%, rgba(249, 115, 22, 0.12) 0%, transparent 50%)`,
          }}
        />
      </div>
      
      <div className="relative z-10 max-w-6xl mx-auto px-6 py-16 md:py-20 text-center">
      {/* Code accent */}
      <motion.p 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-4"
        style={{ 
          fontFamily: v5.fonts.mono,
          fontSize: '12px',
          color: 'rgba(255, 255, 255, 0.4)'
        }}
      >
        // tappd_rewards
      </motion.p>
      
      {/* Badge */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
        style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
      >
        <Trophy className="w-5 h-5" style={{ color: v5.colors.orange }} />
        <span 
          className="font-medium"
          style={{ color: 'rgba(255, 255, 255, 0.9)', fontFamily: v5.fonts.body }}
        >
          Earn Taps, Win Prizes
        </span>
      </motion.div>
      
      {/* Title */}
      <motion.h1 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="text-4xl md:text-5xl lg:text-6xl mb-4"
        style={{ fontFamily: v5.fonts.display, fontWeight: 500, color: v5.colors.white }}
      >
        Tappd Rewards
      </motion.h1>
      <motion.p 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="text-lg max-w-md mx-auto mb-8"
        style={{ fontFamily: v5.fonts.body, color: 'rgba(255, 255, 255, 0.6)' }}
      >
        Compete for monthly prizes at your favorite local spots
      </motion.p>
      
      {/* Quick Stats */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="flex items-center justify-center gap-6 md:gap-10"
      >
        <div className="text-center">
          <div 
            className="text-3xl md:text-4xl font-bold"
            style={{ color: v5.colors.orange, fontFamily: v5.fonts.body }}
          >
            {tapsBalance}
          </div>
          <div style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '14px', fontFamily: v5.fonts.body }}>Total Taps</div>
        </div>
        <div className="w-px h-12" style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}></div>
        <div className="text-center">
          <div 
            className="text-3xl md:text-4xl font-bold"
            style={{ color: v5.colors.green, fontFamily: v5.fonts.body }}
          >
            {unlockedHubsCount}
          </div>
          <div style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '14px', fontFamily: v5.fonts.body }}>Hubs Unlocked</div>
        </div>
        <div className="w-px h-12" style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}></div>
        <div className="text-center">
          <div 
            className="text-3xl md:text-4xl font-bold"
            style={{ color: v5.colors.amber, fontFamily: v5.fonts.body }}
          >
            {questsCompleted}
          </div>
          <div style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '14px', fontFamily: v5.fonts.body }}>Quests Done</div>
        </div>
      </motion.div>
    </div>
    
    {/* V5 Purple-Orange Accent Line */}
    <div 
      className="absolute bottom-0 left-0 w-full h-1" 
      style={{ background: `linear-gradient(to right, ${v5.colors.purple} 0%, ${v5.colors.orange} 100%)` }} 
    />
  </section>
  );
};

export default Rewards;
